import BrandMark from "../components/brand/BrandMark";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { 
  Car, Users, CreditCard, QrCode, LogOut, 
  BarChart3, ArrowLeft, User, Phone, Mail,
  AlertTriangle, Save, Loader2, Download, 
  Copy, CheckCircle, Send, MessageSquare
} from "lucide-react";

const API = '/api';

const AdminAddUser = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Form, 2: Result
  const [result, setResult] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    // User
    full_name: "",
    email: "",
    phone: "",
    whatsapp: "",
    // Vehicle
    vehicle_name: "",
    plate_number: "",
    primary_contact: "",
    // Payment
    amount: 50
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.full_name || !formData.email) {
      toast.error("Completa los datos del usuario");
      return;
    }
    if (!formData.vehicle_name || !formData.plate_number) {
      toast.error("Completa los datos del vehículo");
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem("avisocar_token");
      const response = await axios.post(`${API}/admin/users/create-complete`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult(response.data);
      setStep(2);
      toast.success("Usuario creado exitosamente!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear usuario");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
    toast.success("Copiado al portapapeles");
  };

  const downloadQR = async (type) => {
    if (!result) return;
    const url = type === 'png' 
      ? `${API}/public/qr/${result.vehicle.qr_token}/image`
      : `${API}/public/qr/${result.vehicle.qr_token}/pdf`;
    
    window.open(url, '_blank');
  };

  const sendEmail = async () => {
    if (!result) return;
    setSendingEmail(true);
    
    try {
      const token = localStorage.getItem("avisocar_token");
      await axios.post(`${API}/admin/users/${result.user.id}/send-qr-email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Email enviado correctamente!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar email");
    } finally {
      setSendingEmail(false);
    }
  };

  const openWhatsApp = async () => {
    if (!result) return;
    
    try {
      const token = localStorage.getItem("avisocar_token");
      const response = await axios.post(`${API}/admin/users/${result.user.id}/send-qr-whatsapp`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.open(response.data.whatsapp_link, '_blank');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al abrir WhatsApp");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#0B2545]">
      {/* Sidebar */}


      {/* Main Content */}
      <main className="p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Link to="/admin/users" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-4">
              <ArrowLeft className="w-4 h-4" />
              Volver a Usuarios
            </Link>
            <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {step === 1 ? "Agregar Nuevo Usuario" : "Usuario Creado"}
            </h1>
            <p className="text-slate-400 mt-1">
              {step === 1 ? "Crear usuario con vehículo y QR activo" : "Comparte las credenciales y el QR con el usuario"}
            </p>
          </div>

          {step === 1 ? (
            /* Form Step */
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* User Info */}
              <Card className="bg-[#14375F] border-white/15">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <User className="w-5 h-5 text-[#5BC0FF]" />
                    Información del Usuario
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Nombre Completo *</Label>
                    <Input
                      value={formData.full_name}
                      onChange={(e) => handleChange("full_name", e.target.value)}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="Juan Pérez"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="juan@email.com"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Teléfono (opcional)</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="987654321"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">WhatsApp</Label>
                    <Input
                      value={formData.whatsapp}
                      onChange={(e) => handleChange("whatsapp", e.target.value)}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="987654321 (opcional)"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Vehicle Info */}
              <Card className="bg-[#14375F] border-white/15">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Car className="w-5 h-5 text-blue-400" />
                    Información del Vehículo
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Nombre *</Label>
                    <Input
                      value={formData.vehicle_name}
                      onChange={(e) => handleChange("vehicle_name", e.target.value)}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="Mi Auto Rojo"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Placa *</Label>
                    <Input
                      value={formData.plate_number}
                      onChange={(e) => handleChange("plate_number", e.target.value.toUpperCase())}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="ABC-123"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-slate-300">Destinatarios de avisos (opcional)</Label>
                    <Input
                      value={formData.primary_contact}
                      onChange={(e) => handleChange("primary_contact", e.target.value)}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="987654321"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* iter65 — Contactos de emergencia PURGADOS del alta admin */}

              {/* Payment */}
              <Card className="bg-[#14375F] border-white/15">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-[#5BC0FF]" />
                    Pago
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="max-w-xs">
                    <Label className="text-slate-300">Monto (S/.)</Label>
                    <Input
                      type="number"
                      value={formData.amount}
                      onChange={(e) => handleChange("amount", parseFloat(e.target.value))}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <p className="text-sm text-[#5BC0FF] mt-2">
                    ✓ El pago se marcará como aprobado automáticamente
                  </p>
                </CardContent>
              </Card>

              {/* Submit */}
              <div className="flex gap-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-[#1B84FF] hover:bg-[#0F6FDB] px-8"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Crear Usuario y Generar QR
                    </>
                  )}
                </Button>
              </div>
            </form>
          ) : (
            /* Result Step */
            <div className="space-y-6">
              {/* Success Banner */}
              <Card className="bg-[#1B84FF]/20 border-emerald-500/50">
                <CardContent className="py-6 text-center">
                  <CheckCircle className="w-16 h-16 text-[#5BC0FF] mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-[#5BC0FF]">¡Usuario Creado Exitosamente!</h2>
                  <p className="text-emerald-300 mt-2">El QR está activo y listo para usar</p>
                </CardContent>
              </Card>

              {/* Credentials */}
              <Card className="bg-[#14375F] border-white/15">
                <CardHeader>
                  <CardTitle className="text-white">Credenciales del Usuario</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-slate-700/50 rounded-lg p-4">
                      <Label className="text-slate-400">Email</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-white font-mono">{result?.user?.email}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(result?.user?.email, 'email')}
                          className="text-slate-400 hover:text-white"
                        >
                          {copiedField === 'email' ? <CheckCircle className="w-4 h-4 text-[#5BC0FF]" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-4">
                      <Label className="text-slate-400">Contraseña</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-white font-mono">{result?.user?.password}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(result?.user?.password, 'password')}
                          className="text-slate-400 hover:text-white"
                        >
                          {copiedField === 'password' ? <CheckCircle className="w-4 h-4 text-[#5BC0FF]" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="bg-amber-500/20 border border-amber-500/50 rounded-lg p-4">
                    <p className="text-amber-400 text-sm">
                      ⚠️ Guarda esta contraseña. El usuario puede cambiarla después desde su dashboard.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* QR Code */}
              <Card className="bg-[#14375F] border-white/15">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <QrCode className="w-5 h-5 text-[#5BC0FF]" />
                    Código QR
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col md:flex-row gap-6 items-center">
                    {/* QR Preview */}
                    <div className="bg-white p-4 rounded-lg">
                      <img 
                        src={`${API}/public/qr/${result?.vehicle?.qr_token}/image`}
                        alt="QR Code"
                        className="w-48 h-48"
                      />
                    </div>
                    
                    {/* Actions */}
                    <div className="flex-1 space-y-4">
                      <div>
                        <Label className="text-slate-400">Vehículo</Label>
                        <p className="text-white font-bold text-lg">{result?.vehicle?.name}</p>
                      </div>
                      
                      <div>
                        <Label className="text-slate-400">URL Pública</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-[#5BC0FF] text-sm break-all">{result?.qr_links?.public_url}</p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(result?.qr_links?.public_url, 'url')}
                            className="text-slate-400 hover:text-white"
                          >
                            {copiedField === 'url' ? <CheckCircle className="w-4 h-4 text-[#5BC0FF]" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                      
                      {/* Download buttons */}
                      <div className="flex flex-wrap gap-3">
                        <Button
                          onClick={() => downloadQR('png')}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Descargar PNG
                        </Button>
                        <Button
                          onClick={() => downloadQR('pdf')}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Descargar PDF
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Send Options */}
              <Card className="bg-[#14375F] border-white/15">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Send className="w-5 h-5 text-[#5BC0FF]" />
                    Enviar al Usuario
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-4">
                  <Button
                    onClick={sendEmail}
                    disabled={sendingEmail}
                    className="bg-[#1B84FF] hover:bg-[#0F6FDB]"
                  >
                    {sendingEmail ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4 mr-2" />
                    )}
                    Enviar por Email
                  </Button>
                  <Button
                    onClick={openWhatsApp}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Enviar por WhatsApp
                  </Button>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex gap-4">
                <Button
                  onClick={() => {
                    setStep(1);
                    setResult(null);
                    setFormData({
                      full_name: "", email: "", phone: "", whatsapp: "",
                      vehicle_name: "", plate_number: "", primary_contact: "",
                      emergency_contact_1_name: "", emergency_contact_1_phone: "",
                      emergency_contact_2_name: "", emergency_contact_2_phone: "",
                      emergency_contact_3_name: "", emergency_contact_3_phone: "",
                      amount: 50
                    });
                  }}
                  className="bg-slate-700 hover:bg-slate-600"
                >
                  Agregar Otro Usuario
                </Button>
                <Button
                  onClick={() => navigate("/admin/users")}
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  Volver a Lista
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminAddUser;
